import Axios from 'axios';
import * as React from 'react';
import { RootContext } from '../contexts/root';
import { log } from '../utils/log';
import { StorageUtil } from '../utils/storage';

const envJsonUrl = '/env.json';
const axiosInstance = Axios.create();
const cacheStore = StorageUtil.inTemporaryStore();

export type EnvKey = string;
export type EnvValue =
  | string
  | number
  | boolean
  | EnvValue[]
  | { [key: string]: EnvValue };

export interface HookEnvInterface {
  getEnv<Env = EnvValue>(key: EnvKey): Promise<Env>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function keyInData<Env>(key: string, data?: any): data is Env {
  return key in data;
}

export function useEnv(): HookEnvInterface {
  const { appName, appVersion } = React.useContext(RootContext);

  const cacheKey = React.useMemo(
    () => `× ENV × ${appName} @ ${appVersion}`,
    [appName, appVersion],
  );

  const getEnv = React.useCallback(
    <Env = EnvValue>(key: EnvKey) => {
      if (cacheStore.hasItem(cacheKey)) {
        const data = cacheStore.getItem<Env>(cacheKey);
        if (!data?.hasOwnProperty?.(key)) {
          return Promise.reject(`Variável "${key}" inexistente no ENV!`);
        }
        return Promise.resolve(data[key as keyof Env] as Env);
      }

      const request = axiosInstance.get<Env>(envJsonUrl).then(({ data }) => {
        if (!keyInData<Env>(key, data)) {
          throw Error(`Variável "${key}" inexistente no ENV!`);
        }
        cacheStore.setItem(cacheKey, data);
        return data[key as keyof Env] as Env;
      });

      request.catch((error) =>
        log(
          'ENV',
          `Erro ao achar o ${envJsonUrl}. Certifique-se que o arquivo está publicado.`,
          error,
        ),
      );
      return request;
    },
    [cacheKey],
  );

  return {
    getEnv,
  };
}
